import * as React from "react"
import { XIcon } from "lucide-react"
import { cn } from "../../lib/utils"
import { useEffect, createContext, useContext, useRef } from "react"

// Dialog Context to share onOpenChange with children
interface DialogContextType {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const DialogContext = createContext<DialogContextType | null>(null)

// Global counter to track open dialogs
let openDialogCount = 0
let originalBodyOverflow = ''
let originalBodyPaddingRight = ''
let isLocked = false

// Simple Dialog Root - manages open state
interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
}

function Dialog({ open, onOpenChange, children }: DialogProps) {
  // Prevent body scroll when dialog is open - with proper cleanup
  useEffect(() => {
    if (open) {
      // First dialog to open - store original values and lock scroll
      if (openDialogCount === 0 && !isLocked) {
        originalBodyOverflow = document.body.style.overflow || ''
        originalBodyPaddingRight = document.body.style.paddingRight || ''
        
        // Calculate scrollbar width to prevent layout shift
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
        
        // Lock body scroll
        document.body.style.overflow = 'hidden'
        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = `${scrollbarWidth}px`
        }
        isLocked = true
      }
      
      openDialogCount++
      
      return () => {
        openDialogCount = Math.max(0, openDialogCount - 1)
        
        // Always restore when this dialog closes, if it's the last one
        if (openDialogCount === 0 && isLocked) {
          document.body.style.overflow = originalBodyOverflow
          document.body.style.paddingRight = originalBodyPaddingRight
          isLocked = false
        }
      }
    } else {
      // Dialog is closed - ensure scroll is restored if no other dialogs are open
      if (openDialogCount === 0 && isLocked) {
        document.body.style.overflow = originalBodyOverflow
        document.body.style.paddingRight = originalBodyPaddingRight
        isLocked = false
      }
    }
  }, [open])
  
  // Safety: restore scroll on unmount
  useEffect(() => {
    return () => {
      openDialogCount = Math.max(0, openDialogCount - 1)
      if (openDialogCount === 0 && isLocked) {
        document.body.style.overflow = originalBodyOverflow
        document.body.style.paddingRight = originalBodyPaddingRight
        isLocked = false
      }
    }
  }, [])
  
  // Safety: restore scroll when page becomes visible (handles edge cases)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && openDialogCount === 0 && isLocked) {
        // Check if body is still locked and restore if needed
        document.body.style.overflow = originalBodyOverflow
        document.body.style.paddingRight = originalBodyPaddingRight
        isLocked = false
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // Handle ESC key to close
  useEffect(() => {
    if (!open) return
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false)
      }
    }
    
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open, onOpenChange])

  return (
    <DialogContext.Provider value={{ open, onOpenChange }}>
      {children}
    </DialogContext.Provider>
  )
}

// Dialog Overlay - backdrop
interface DialogOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
}

const DialogOverlay = React.forwardRef<HTMLDivElement, DialogOverlayProps>(
  ({ className, onClick, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "fixed inset-0 z-[99998] bg-black/50 backdrop-blur-sm",
        "animate-in fade-in-0 duration-200",
        className
      )}
      onClick={onClick}
      style={{ pointerEvents: 'auto' }}
      {...props}
    />
  )
)
DialogOverlay.displayName = "DialogOverlay"

// Dialog Content - the actual dialog box
interface DialogContentProps extends React.HTMLAttributes<HTMLDivElement> {
  showCloseButton?: boolean
  onClose?: () => void
  disableOverlayClose?: boolean
}

const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(
  ({ className, children, showCloseButton = true, onClose, disableOverlayClose = false, ...props }, ref) => {
    const dialogContext = useContext(DialogContext)
    const handleClose = () => {
      if (onClose) {
        onClose()
      } else if (dialogContext) {
        dialogContext.onOpenChange(false)
      }
    }

    const handleOverlayClick = (e: React.MouseEvent) => {
      // Only close if clicking directly on overlay, not on content, and overlay close is enabled
      if (!disableOverlayClose && e.target === e.currentTarget) {
        handleClose()
      }
    }

    // Check if dialog should be open
    if (!dialogContext?.open) return null;

    return (
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 overflow-y-auto" style={{ pointerEvents: 'none' }}>
        <DialogOverlay onClick={handleOverlayClick} />
        <div
          ref={ref}
          className={cn(
            "relative z-[100000] bg-background w-full max-w-screen-xl my-auto",
            "rounded-lg border shadow-lg p-6",
            "animate-in fade-in-0 zoom-in-95 duration-200",
            "max-h-[90vh] overflow-y-auto",
            className
          )}
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
          style={{ pointerEvents: 'auto' }}
      {...props}
    >
      {children}
      {showCloseButton && (
            <button
              type="button"
              onClick={handleClose}
              className="absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
            >
              <XIcon className="h-4 w-4" />
          <span className="sr-only">Close</span>
            </button>
      )}
        </div>
      </div>
    )
  }
)
DialogContent.displayName = "DialogContent"

// Dialog Header
function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  )
}

// Dialog Footer
function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    />
  )
}

// Dialog Title
function DialogTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2
      className={cn("text-lg font-semibold leading-none tracking-tight", className)}
      {...props}
    />
  )
}

// Dialog Description
function DialogDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

// Dialog Close - for programmatic closing
const DialogClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn("rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2", className)}
    {...props}
  />
))
DialogClose.displayName = "DialogClose"

// Dialog Trigger - simple wrapper
function DialogTrigger({ 
  children, 
  onClick,
  asChild // Accept asChild but ignore it for now or implement if needed
}: { 
  children: React.ReactNode
  onClick?: () => void
  asChild?: boolean 
}) {
  const context = useContext(DialogContext)
  
  const handleClick = () => {
    if (onClick) onClick()
    if (context) context.onOpenChange(true)
  }

  return (
    <div onClick={handleClick} style={{ cursor: 'pointer', display: 'inline-block' }}>
      {children}
    </div>
  )
}

// Dialog Portal - not needed with our simple implementation, but kept for compatibility
function DialogPortal({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
